import _ from "lodash";
import { normalizeSkuPhotoMappingTargets } from "../drive/skuPhotoMappingUtils";
import { getAmbifi3dEntityConfig } from "../../utils/ambifi3dAuthoringUtils";
import {
	getSkuPhotoMappingCatalogId,
	ICH_SKU_PHOTO_MAPPING_CATALOG_ID,
	ICH_SKU_PHOTO_MAPPING_ORG_ID,
	isSkuPhotoMappingOrganization,
	JAB_CC_SKU_PHOTO_MAPPING_CATALOG_ID,
	JAB_CC_SKU_PHOTO_MAPPING_ORG_ID,
	SKU_PHOTO_MAPPING_ORG_IDS
} from "../../utils/skuPhotoMappingOrganizations";

export const ICH_ORG_ID = ICH_SKU_PHOTO_MAPPING_ORG_ID;
export const JAB_CC_ORG_ID = JAB_CC_SKU_PHOTO_MAPPING_ORG_ID;
export {
	getSkuPhotoMappingCatalogId,
	ICH_SKU_PHOTO_MAPPING_CATALOG_ID,
	isSkuPhotoMappingOrganization,
	JAB_CC_SKU_PHOTO_MAPPING_CATALOG_ID,
	SKU_PHOTO_MAPPING_ORG_IDS
};
export const ICH_STRUCTURAL_ACTIVITY_ID = "caeea8b7-1e4d-5cc6-98ee-22e1d7597ae5";
export const SKU_PHOTO_MAPPING_PROTOCOL_VERSION = 1;
export const SKU_PHOTO_MAPPING_VIEW_MODE_PROCEDURE = "procedure";
export const SKU_PHOTO_MAPPING_VIEW_MODE_CATALOG = "catalog";

const ICH_STRUCTURAL_ACTIVITY_NAME = "ich - structural selection";
const ICH_STRUCTURAL_ACTIVITY_TAGS = ["ich", "structural", "sku-filters"];
const SKU_PHOTO_MAPPING_ACTIVITY_TAG = "sku-photo-mapping-workbench";
const DEFAULT_ACTIVITY_DISCOVERY_CONCURRENCY = 6;

function isTrue(value) {
	return value === true || _.toString(value || "").trim().toLowerCase() === "true";
}

function isEnabledValue(value) {
	if (_.isBoolean(value)) {
		return value;
	}
	if (_.isNumber(value)) {
		return value === 1;
	}
	return ["true", "1", "yes", "enabled"].includes(
		_.toString(value || "").trim().toLowerCase()
	);
}

function getActivityAuthorProperties(activity = {}) {
	const authorProperties = _.get(activity, "authorProperties");
	if (_.isPlainObject(authorProperties)) {
		return authorProperties;
	}
	if (_.isString(authorProperties) && authorProperties.trim()) {
		try {
			const parsed = JSON.parse(authorProperties);
			if (_.isPlainObject(parsed)) {
				return parsed;
			}
			if (_.isArray(parsed)) {
				return parsed.reduce((result, entry) => {
					const key = _.toString(_.get(entry, "key") || _.get(entry, "name") || _.get(entry, "id") || "").trim();
					if (key) {
						result[key] = _.get(entry, "value");
					}
					return result;
				}, {});
			}
			return null;
		} catch (err) {
			return null;
		}
	}
	return null;
}

function hasExplicitPricingSplitScreen(activity = {}) {
	const authorProperties = getActivityAuthorProperties(activity);
	return !!authorProperties
		&& Object.prototype.hasOwnProperty.call(authorProperties, "pricingSplitScreen");
}

function isPricingSplitScreenEnabled(activity = {}) {
	const authorProperties = getActivityAuthorProperties(activity);
	return !!authorProperties && authorProperties.pricingSplitScreen === true;
}

export function isSkuPhotoMappingMaterialLabActivity(activity = {}) {
	const author3dConfig = getAmbifi3dEntityConfig(activity);
	if (!author3dConfig || !isEnabledValue(_.get(author3dConfig, "splitScreen.enabled"))) {
		return false;
	}

	const explicitMaterialLabValue = [
		_.get(author3dConfig, "splitScreen.materialLabAsActivity"),
		_.get(author3dConfig, "splitScreen.useMaterialLabAsActivity"),
		_.get(author3dConfig, "splitScreen.materialLabOnly"),
		_.get(author3dConfig, "materialLabAsActivity")
	].find((value) => value !== undefined && value !== null && value !== "");
	if (explicitMaterialLabValue !== undefined) {
		return isEnabledValue(explicitMaterialLabValue);
	}

	const activityPaneMode = _.toString(_.get(author3dConfig, "splitScreen.activityPaneMode", ""))
		.trim()
		.toLowerCase()
		.replace(/[_\s-]+/g, "");
	return activityPaneMode === "materiallab";
}

function isActivityMarkedReadOnly(activity = {}) {
	return [
		_.get(activity, "readOnly"),
		_.get(activity, "isReadOnly"),
		_.get(activity, ["launchContext", "readOnly"])
	].some(isTrue);
}

function normalizeActivityTags(activity = {}) {
	return (_.isArray(activity.tags) ? activity.tags : [])
		.map((tag) => _.toString(_.isPlainObject(tag) ? (tag.id || tag.name || tag.value) : tag).trim().toLowerCase())
		.filter(Boolean);
}

export function isSkuPhotoMappingActivity(activity = {}) {
	if (!_.isPlainObject(activity)) {
		return false;
	}
	const authorProperties = getActivityAuthorProperties(activity);
	if (isPricingSplitScreenEnabled(activity)) {
		return true;
	}

	const activityId = _.toString(activity.id || "").trim();
	if (activityId === ICH_STRUCTURAL_ACTIVITY_ID) {
		return true;
	}

	if (isEnabledValue(_.get(authorProperties, "skuPhotoMappingWorkbench"))) {
		return true;
	}

	const activityName = _.toString(activity.name || activity.title || "").trim().toLowerCase();
	const tags = normalizeActivityTags(activity);
	return tags.includes(SKU_PHOTO_MAPPING_ACTIVITY_TAG)
		|| (activityName.startsWith(ICH_STRUCTURAL_ACTIVITY_NAME)
			&& ICH_STRUCTURAL_ACTIVITY_TAGS.every((tag) => tags.includes(tag)));
}

export function isSkuPhotoMappingActivityEligible(activity = {}, {
	readOnly = false,
	requireExplicitPricingSplitScreen = false
} = {}) {
	if (!_.isPlainObject(activity)
		|| !_.toString(activity.id || "").trim()
		|| _.get(activity, "newDoc") === true
		|| readOnly === true
		|| isActivityMarkedReadOnly(activity)
		|| isSkuPhotoMappingMaterialLabActivity(activity)) {
		return false;
	}

	if (isPricingSplitScreenEnabled(activity)) {
		return true;
	}
	const authorProperties = getActivityAuthorProperties(activity);
	if (requireExplicitPricingSplitScreen || authorProperties) {
		return false;
	}

	// Preserve compatibility for the canonical/tagged Structural activities whose
	// legacy direct-launch records predate authorProperties. Picker discovery uses
	// requireExplicitPricingSplitScreen so it never relies on this fallback.
	return isSkuPhotoMappingActivity(activity);
}

export function shouldResolveSkuPhotoMappingActivityMetadata(activity = {}) {
	if (!_.isPlainObject(activity)
		|| !_.toString(activity.id || "").trim()
		|| _.get(activity, "newDoc") === true
		|| isActivityMarkedReadOnly(activity)
		|| isSkuPhotoMappingMaterialLabActivity(activity)) {
		return false;
	}
	return !hasExplicitPricingSplitScreen(activity);
}

export function getSkuPhotoMappingActivityCandidates(activities = [], options = {}) {
	const candidatesById = new Map();
	(_.isArray(activities) ? activities : []).forEach((activity) => {
		const activityId = _.toString(_.get(activity, "id") || "").trim();
		if (!activityId || !isSkuPhotoMappingActivityEligible(activity, options)) {
			return;
		}
		candidatesById.set(activityId, activity);
	});

	return Array.from(candidatesById.values()).sort((left, right) => {
		const leftIsCanonical = _.toString(left.id || "").trim() === ICH_STRUCTURAL_ACTIVITY_ID;
		const rightIsCanonical = _.toString(right.id || "").trim() === ICH_STRUCTURAL_ACTIVITY_ID;
		if (leftIsCanonical !== rightIsCanonical) {
			return leftIsCanonical ? -1 : 1;
		}

		const leftTimestamp = Number(left.revisionTs || left.updatedAt || left.createdAt || 0) || 0;
		const rightTimestamp = Number(right.revisionTs || right.updatedAt || right.createdAt || 0) || 0;
		if (leftTimestamp !== rightTimestamp) {
			return rightTimestamp - leftTimestamp;
		}

		return _.toString(left.name || left.title || left.id).localeCompare(
			_.toString(right.name || right.title || right.id)
		);
	});
}

/**
 * Inventory rows intentionally omit most authorProperties. Resolve only those
 * unresolved rows when the picker opens, with bounded concurrency so a large
 * activity library cannot flood the metadata endpoint.
 */
export async function discoverSkuPhotoMappingActivityCandidates(
	activities = [],
	loadActivityMetadata,
	options = {}
) {
	const inventoryActivities = _.uniqBy(
		(_.isArray(activities) ? activities : []).filter((activity) => (
			_.isPlainObject(activity) && _.toString(activity.id || "").trim()
		)),
		(activity) => _.toString(activity.id || "").trim()
	);
	const unresolvedActivities = inventoryActivities.filter(shouldResolveSkuPhotoMappingActivityMetadata);
	const unresolvedActivityIds = new Set(unresolvedActivities.map((activity) => _.toString(activity.id || "").trim()));
	const resolvedActivities = [];
	const failures = [];
	const requestedConcurrency = Number(options.concurrency) || DEFAULT_ACTIVITY_DISCOVERY_CONCURRENCY;
	const concurrency = Math.max(1, Math.min(requestedConcurrency, unresolvedActivities.length || 1));
	const onProgress = _.isFunction(options.onProgress) ? options.onProgress : null;
	let nextIndex = 0;
	let completedCount = 0;

	if (unresolvedActivities.length > 0 && !_.isFunction(loadActivityMetadata)) {
		throw new Error("An activity metadata loader is required to discover SKU photo-mapping activities.");
	}

	const workers = Array.from({ length: concurrency }, async () => {
		while (nextIndex < unresolvedActivities.length) {
			const activity = unresolvedActivities[nextIndex];
			nextIndex += 1;
			try {
				const metadata = await loadActivityMetadata(activity);
				if (!_.isPlainObject(metadata)) {
					throw new Error("Activity metadata was unavailable.");
				}
				resolvedActivities.push({
					...activity,
					...metadata,
					id: _.toString(activity.id).trim()
				});
			} catch (err) {
				failures.push({
					activityId: _.toString(activity.id || "").trim(),
					activityName: _.toString(activity.name || activity.title || activity.id || "").trim(),
					error: _.toString(_.get(err, "message", err) || "Unable to load activity metadata.")
				});
			} finally {
				completedCount += 1;
				if (onProgress) {
					onProgress({
						completedCount,
						totalCount: unresolvedActivities.length,
						failureCount: failures.length
					});
				}
			}
		}
	});

	await Promise.all(workers);
	return {
		activities: getSkuPhotoMappingActivityCandidates([
			...inventoryActivities.filter((activity) => !unresolvedActivityIds.has(_.toString(activity.id || "").trim())),
			...resolvedActivities
		], { requireExplicitPricingSplitScreen: true }),
		inspectedCount: unresolvedActivities.length,
		failureCount: failures.length,
		failures
	};
}

export function mergeSkuPhotoMappingActivityMetadata(activities = [], activity = {}) {
	const activityId = _.toString(_.get(activity, "id") || "").trim();
	const safeActivities = _.isArray(activities) ? activities : [];
	if (!activityId) {
		return safeActivities;
	}

	let found = false;
	const nextActivities = safeActivities.map((existingActivity) => {
		if (_.toString(_.get(existingActivity, "id") || "").trim() !== activityId) {
			return existingActivity;
		}
		found = true;
		const mergedActivity = {
			...existingActivity,
			...activity,
			id: activityId
		};
		[
			"visible",
			"dependency",
			"newDoc",
			"identityId",
			"ownerIdentityId",
			"sharedWithId",
			"sharedWithType"
		].forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(existingActivity, property)) {
				mergedActivity[property] = existingActivity[property];
			}
		});
		return mergedActivity;
	});
	return found ? nextActivities : [...nextActivities, { ...activity, id: activityId }];
}

export function findSkuPhotoMappingActivityById(activities = [], activityId = "") {
	const normalizedActivityId = _.toString(activityId || "").trim();
	if (!normalizedActivityId) {
		return null;
	}
	return (_.isArray(activities) ? activities : []).find((activity) => (
		_.toString(_.get(activity, "id") || "").trim() === normalizedActivityId
	)) || null;
}

export function resolveSkuPhotoMappingActivity(activities = [], preferredActivityId = "") {
	const candidates = getSkuPhotoMappingActivityCandidates(activities);
	const preferredId = _.toString(preferredActivityId || "").trim();
	return candidates.find((activity) => _.toString(activity.id || "").trim() === preferredId)
		|| candidates[0]
		|| null;
}

export function isSkuPhotoMappingRequested(search = "") {
	try {
		const params = new URLSearchParams(_.toString(search || ""));
		const value = _.toString(params.get("skuPhotoMapping") || "").trim().toLowerCase();
		return value === "1" || value === "true" || value === "yes";
	} catch (err) {
		return false;
	}
}

export function isSkuPhotoMappingEnabled({ search, orgId, activity, readOnly = false } = {}) {
	return isSkuPhotoMappingRequested(search)
		&& isSkuPhotoMappingOrganization(orgId)
		&& isSkuPhotoMappingActivityEligible(activity, { readOnly });
}

export function normalizeSkuPhotoMappingViewMode(value) {
	return _.toString(value || "").trim().toLowerCase() === SKU_PHOTO_MAPPING_VIEW_MODE_CATALOG
		? SKU_PHOTO_MAPPING_VIEW_MODE_CATALOG
		: SKU_PHOTO_MAPPING_VIEW_MODE_PROCEDURE;
}

export function normalizeSkuPhotoMappingSelectionPayload(payload, trustedContext = {}) {
	if (!_.isPlainObject(payload)
		|| payload.protocolVersion !== SKU_PHOTO_MAPPING_PROTOCOL_VERSION
		|| !Array.isArray(payload.selections)) {
		return null;
	}

	const contextId = _.toString(payload.contextId || "").trim();
	if (!contextId) {
		return null;
	}

	const validSelections = payload.selections.every((selection) => (
		_.isPlainObject(selection)
		&& _.toString(selection.objectID || "").trim() !== ""
		&& _.toString(selection.sku || "").trim() !== ""
	));
	if (!validSelections) {
		return null;
	}

	return {
		...payload,
		method: "skuPhotoMappingSelectionChanged",
		protocolVersion: SKU_PHOTO_MAPPING_PROTOCOL_VERSION,
		contextId,
		indexName: _.toString(payload.indexName || "").trim(),
		pricingSnapshotId: _.toString(payload.pricingSnapshotId || "").trim(),
		selections: payload.selections,
		orgId: _.toString(trustedContext.orgId || "").trim(),
		activityId: _.toString(trustedContext.activityId || "").trim()
	};
}

export function getSkuPhotoMappingHeaderModel(selections = [], selectedPhotoCount = 0, options = {}) {
	const targets = normalizeSkuPhotoMappingTargets(selections, options);
	const viewMode = normalizeSkuPhotoMappingViewMode(options.viewMode);
	const photoCount = Math.max(0, Number(selectedPhotoCount) || 0);
	const targetLabels = targets.map((target) => {
		const sku = _.toString(target.sku || "").trim();
		const description = _.toString(
			_.get(target, ["display", "desc"])
			|| _.get(target, ["display", "subtitle"])
			|| ""
		).trim();
		return description ? `${sku} — ${description}` : sku;
	}).filter(Boolean);
	const targetPreviewLimit = 2;
	const targetOverflowCount = Math.max(0, targetLabels.length - targetPreviewLimit);
	const targetPreview = targetLabels.slice(0, targetPreviewLimit).join("; ");
	const skuCount = targets.length;
	const readyForReview = skuCount > 0 && photoCount > 0;
	const steps = [
		{
			id: "skus",
			number: 1,
			label: "Choose SKUs",
			value: skuCount > 0 ? `${skuCount} selected` : "Required",
			complete: skuCount > 0,
			active: skuCount === 0
		},
		{
			id: "photos",
			number: 2,
			label: "Choose photos",
			value: photoCount > 0 ? `${photoCount} selected` : "Required",
			complete: photoCount > 0,
			active: skuCount > 0 && photoCount === 0
		},
		{
			id: "review",
			number: 3,
			label: "Review & apply",
			value: readyForReview ? "Ready" : "Waiting",
			complete: false,
			active: readyForReview
		}
	];

	let nextStep;
	if (skuCount === 0) {
		nextStep = viewMode === SKU_PHOTO_MAPPING_VIEW_MODE_CATALOG
			? "Search or browse the catalog, then select one or more SKUs."
			: "Choose a procedure section, then select one or more SKUs.";
	} else if (photoCount === 0) {
		nextStep = "Search or browse Drive, then select one or more matching photos.";
	} else {
		nextStep = "Review the proposed mappings and metadata before applying them.";
	}

	return {
		viewMode,
		skuCount,
		photoCount,
		targetLabels,
		targetPreview,
		targetOverflowCount,
		readyForReview,
		steps,
		nextStep
	};
}

export function getSkuPhotoMappingLaunchPath(activityId = ICH_STRUCTURAL_ACTIVITY_ID) {
	return `/activity/${encodeURIComponent(activityId)}?skuPhotoMapping=1`;
}
